import React from "react";
import styled from "styled-components";
import { UI_BODY_SEMIBOLD, UI_BODY_SM, UI_CAPTION_DF, colors2 } from "oolib";
import { DatasetCircles } from "./DatasetCircles";

/**
 * The topic card: title + description, a segmented bar with its legend,
 * and a bottom-pinned footer (observation count left, dataset circles
 * right). The Topics cards grid draws every topic with it; the signal
 * category cards draw with it too, with `footerLeft` in place of the
 * observation count, `headerRight` beside the title (a share change) and
 * `stat` between the header and the bar (the big number). Otherwise it is
 * the topic card it always was.
 */

// No border at rest: the grey ground behind the grid already separates the
// cards. Clickable variants reveal one on hover, like the Annotation
// Explorer's cards.
export const Card = styled.div`
  border: 1px solid transparent;
  border-radius: 12px;
  background: ${colors2.white};
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ClickableCard = styled(Card)`
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${colors2.grey20};
  }
`;

export const CardTail = styled.div`
  margin-top: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

export const CardFooter = styled.div`
  padding-top: 4px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

// "12 observations" — the distinct annotations under a node, findings and
// parked alike (the BE folds both into annotationCount). Same caption size as
// the SentimentBar legend, regular weight.
export const ObservationCount = ({ count }) =>
  count > 0 ? (
    <UI_CAPTION_DF style={{ color: colors2.grey40 }}>
      {count} observation{count === 1 ? "" : "s"}
    </UI_CAPTION_DF>
  ) : null;

const capitalize = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

// Signal categories as soft tinted chips — one per category with its count,
// the category's colour as a 12%-tint fill and the text. Replaces the
// sentiment bar; the leading category still carries its trend suffix.
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 11px;
  border-radius: 8px;
  min-width: 0;
  background: color-mix(in srgb, ${({ $c }) => $c} 12%, transparent);
`;
const ChipDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $c }) => $c};
`;
const ChipLabel = styled(UI_CAPTION_DF)`
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $c }) => $c};
`;
const ChipCount = styled(UI_CAPTION_DF)`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${colors2.grey80};
`;

export const SignalChips = ({ segments = [], emphasisKey, emphasisLegendSuffix }) => {
  const visible = segments.filter((s) => s.value > 0 || s.count > 0);
  if (visible.length === 0) return null;
  return (
    <Chips>
      {visible.map((s) => (
        <Chip key={s.key} $c={s.color}>
          <ChipDot $c={s.color} />
          <ChipLabel $c={s.color}>{s.title || capitalize(s.key)}</ChipLabel>
          {s.count != null && <ChipCount>{s.count}</ChipCount>}
        </Chip>
      ))}
    </Chips>
  );
};

export const TopicCard = ({
  title,
  description,
  // The description's face and colour; the topic card's defaults.
  DescriptionTypo = UI_BODY_SM,
  descriptionColor = colors2.grey60,
  headerRight,
  stat,
  segments = [],
  emphasisKey,
  emphasisLegendSuffix,
  count,
  footerLeft,
  // Optional extra beside the dataset circles (e.g. an Ask AI entry).
  footerRight,
  datasets,
  titleByContentType,
  onClick,
}) => {
  const Shell = onClick ? ClickableCard : Card;
  return (
    <Shell onClick={onClick}>
      <Header>
        <div style={{ minWidth: 0 }}>
          <UI_BODY_SEMIBOLD>{title}</UI_BODY_SEMIBOLD>
          {description && <DescriptionTypo style={{ color: descriptionColor }}>{description}</DescriptionTypo>}
        </div>
        {headerRight}
      </Header>
      {stat}
      <SignalChips segments={segments} emphasisKey={emphasisKey} emphasisLegendSuffix={emphasisLegendSuffix} />
      {/* Same bottom-pinned footer as the finding cards: count on the
          left, dataset provenance circles on the right. */}
      <CardTail>
        <CardFooter>
          {footerLeft ?? <ObservationCount count={count} />}
          {footerRight ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {footerRight}
              <DatasetCircles datasets={datasets} titleByContentType={titleByContentType} />
            </div>
          ) : (
            <DatasetCircles datasets={datasets} titleByContentType={titleByContentType} />
          )}
        </CardFooter>
      </CardTail>
    </Shell>
  );
};
